import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SideCart } from '../components/SideCart';
import { ChatBot } from '../components/ChatBot';
import { useCart } from '../context/CartContext';
import { Check } from 'lucide-react';

const shippingRows = [
  { method: 'Standard', time: '3–5 business days', price: 'Free', note: 'No minimum required' },
  { method: 'Express', time: '1–2 business days', price: '$14.99', note: 'Available for all US addresses' },
  { method: 'Subscription orders', time: '3–5 business days', price: 'Free', note: 'Always free, no minimum' },
];

const returnSteps = [
  { n: '01', title: 'Contact us', body: 'Email sales@berleto.com with your order number within 30 days of delivery. Our team will confirm your eligibility and send instructions.' },
  { n: '02', title: 'Ship it back', body: "We'll provide a prepaid return label. Pack the items securely in the original packaging and drop off at any carrier location." },
  { n: '03', title: 'Get refunded', body: 'Refunds are processed within 5–7 business days of us receiving your return. You\'ll receive a confirmation email when it\'s done.' },
];

export function ShippingPage() {
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeItem, cartItemsCount } = useCart();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#FAF9F6' }}>
      <Navigation onCartOpen={() => setIsCartOpen(true)} cartItemsCount={cartItemsCount} />

      {/* Hero */}
      <section className="pt-32 pb-20" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-xs font-medium tracking-[0.3em] uppercase mb-6" style={{ color: '#B8704D' }}>
              Shipping & Returns
            </p>
            <h1 className="text-5xl md:text-6xl font-light leading-tight mb-6" style={{ color: '#1A2A1A' }}>
              Free shipping.<br />
              <span style={{ color: 'rgba(26,42,26,0.28)' }}>Hassle-free returns.</span>
            </h1>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.5)', maxWidth: '44ch' }}>
              We ship free within the United States. All orders include tracking and full insurance from the moment they leave our warehouse.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick facts strip */}
      <section className="border-y" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
            {[
              { n: 'Free', label: 'Shipping on all orders' },
              { n: '1–2 days', label: 'Express available' },
              { n: '30-day', label: 'Return window' },
              { n: '50 states', label: 'Including AK & HI' },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="px-8 py-8"
              >
                <p className="text-2xl font-light mb-1" style={{ color: '#1A2A1A' }}>{s.n}</p>
                <p className="text-xs tracking-widest uppercase" style={{ color: 'rgba(26,42,26,0.4)' }}>{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Shipping table */}
      <section className="py-24" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-xs font-medium tracking-[0.25em] uppercase mb-5" style={{ color: '#B8704D' }}>
                USA Shipping
              </p>
              <h2 className="text-4xl font-light leading-tight mb-12" style={{ color: '#1A2A1A' }}>
                Delivery options.
              </h2>

              <div className="border-t" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                {shippingRows.map((row, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="grid grid-cols-3 py-5 border-b"
                    style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                  >
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#1A2A1A' }}>{row.method}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'rgba(26,42,26,0.4)' }}>{row.note}</p>
                    </div>
                    <p className="text-sm" style={{ color: 'rgba(26,42,26,0.55)' }}>{row.time}</p>
                    <p className="text-sm font-medium text-right" style={{ color: row.price === 'Free' ? '#22c55e' : '#1A2A1A' }}>
                      {row.price}
                    </p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 p-6 border" style={{ borderColor: 'rgba(26,42,26,0.08)', background: '#F0EDE6' }}>
                <p className="text-xs font-medium mb-2" style={{ color: '#1A2A1A' }}>Important</p>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(26,42,26,0.55)' }}>
                  Orders placed before 2pm EST Monday–Friday typically ship the same day. Orders placed on weekends ship the following Monday. All orders include a tracking number sent to your email.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <p className="text-xs font-medium tracking-[0.25em] uppercase mb-5" style={{ color: '#B8704D' }}>
                Coverage
              </p>
              <h2 className="text-4xl font-light leading-tight mb-12" style={{ color: '#1A2A1A' }}>
                Where we ship.
              </h2>

              <div className="space-y-4 mb-8">
                {[
                  'All 50 US states, including Alaska and Hawaii',
                  'US territories (Puerto Rico, Guam, USVI)',
                  'PO Boxes and APO/FPO addresses accepted',
                  'Full tracking on every shipment',
                  'All orders are fully insured',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm" style={{ color: 'rgba(26,42,26,0.65)' }}>
                    <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#22c55e' }} />
                    {item}
                  </div>
                ))}
              </div>

              <div className="p-6 border" style={{ borderColor: 'rgba(26,42,26,0.08)', background: '#F0EDE6' }}>
                <p className="text-xs font-medium mb-2" style={{ color: '#1A2A1A' }}>International shipping</p>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(26,42,26,0.55)' }}>
                  We currently ship within the United States only. International shipping is in development — sign up for our newsletter to be notified when we launch in your country.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Returns */}
      <section className="py-24" style={{ background: '#E8DCC4' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-16"
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ color: '#B8704D' }}>
              Returns
            </p>
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: '#1A2A1A' }}>
              30-day guarantee.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              {returnSteps.map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-6 py-7 border-b"
                  style={{ borderColor: 'rgba(26,42,26,0.1)' }}
                >
                  <span className="text-xs font-medium tracking-widest mt-0.5 flex-shrink-0 w-6" style={{ color: '#B8704D' }}>
                    {step.n}
                  </span>
                  <div>
                    <h3 className="text-base font-medium mb-2" style={{ color: '#1A2A1A' }}>{step.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.6)' }}>{step.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="md:sticky md:top-32"
            >
              <div className="border" style={{ borderColor: 'rgba(26,42,26,0.12)', background: '#FAF9F6' }}>
                <div className="px-8 py-6 border-b" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                  <p className="text-xs font-medium tracking-[0.2em] uppercase mb-1" style={{ color: '#B8704D' }}>Return policy</p>
                </div>
                {[
                  { label: 'Window', value: '30 days from delivery' },
                  { label: 'Unopened items', value: 'Full refund' },
                  { label: 'Opened items', value: 'Store credit' },
                  { label: 'Return shipping', value: 'Prepaid label provided' },
                  { label: 'Refund timeline', value: '5–7 business days' },
                  { label: 'Contact', value: 'sales@berleto.com' },
                ].map((row, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center px-8 py-4 border-b last:border-b-0"
                    style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                  >
                    <span className="text-xs" style={{ color: 'rgba(26,42,26,0.45)' }}>{row.label}</span>
                    <span className="text-sm font-medium" style={{ color: '#1A2A1A' }}>{row.value}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 border-t pt-12"
            style={{ borderColor: 'rgba(26,42,26,0.08)' }}
          >
            <div>
              <p className="text-base font-medium mb-1" style={{ color: '#1A2A1A' }}>Still have questions?</p>
              <p className="text-sm" style={{ color: 'rgba(26,42,26,0.5)' }}>Our team responds within 24 hours.</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/contact')}
                className="px-8 py-3.5 text-sm font-medium tracking-wide transition-colors"
                style={{ background: '#1A2A1A', color: '#FAF9F6' }}
                onMouseEnter={e => e.currentTarget.style.background = '#243424'}
                onMouseLeave={e => e.currentTarget.style.background = '#1A2A1A'}
              >
                Contact Us
              </button>
              <button
                onClick={() => navigate('/faq')}
                className="px-8 py-3.5 text-sm font-medium tracking-wide transition-colors border"
                style={{ borderColor: 'rgba(26,42,26,0.15)', color: 'rgba(26,42,26,0.6)', background: 'transparent' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#1A2A1A'; e.currentTarget.style.color = '#FAF9F6'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(26,42,26,0.6)'; }}
              >
                View FAQ
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}
